import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { DemandesService } from './demandes.service';
import { DemandeAvisValidation, ValidationCounts } from '@models/api/responses';
import { map, catchError } from 'rxjs/operators';
import { DemandeType } from '@models/enums';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly validationCountsSubject = new BehaviorSubject<ValidationCounts>({
    numberRecrutementToValidate: 0,
    numberRenouvellementToValidate: 0,
    totalDemandeToValidate: 0,
    total: 0,
    demandeAvis: [],
    totalDemandeAvis: 0,
    numberRecrutementAvis: 0,
    numberRenouvellementAvis: 0
  });

  validationCounts$: Observable<ValidationCounts> = this.validationCountsSubject.asObservable();

  constructor(private readonly demandesService: DemandesService) {}

  /**
   * Refreshes the validation counts by fetching the latest data from the server
   */
  refreshValidationCounts(isValidateur: boolean): void {
    const demandeValidation$ = isValidateur
      ? this.demandesService.getDemandeToValidateCount().pipe(
          map((result) => ({ success: true, data: result })),
          catchError(() =>
            of({
              success: false,
              data: { numberRecrutementToValidate: 0, numberRenouvellementToValidate: 0 }
            })
          )
        )
      : of({
          success: true,
          data: { numberRecrutementToValidate: 0, numberRenouvellementToValidate: 0 }
        });

    const demandeAvis$ = this.demandesService.getDemandeAvisToValidateCount().pipe(
      map((result) => ({ success: true, data: result })),
      catchError(() =>
        of({
          success: false,
          data: { demandeAvis: [], totalDemandeAvis: 0, numberRecrutementAvis: 0, numberRenouvellementAvis: 0 }
        })
      )
    );

    forkJoin({
      demandeValidation: demandeValidation$,
      demandeAvis: demandeAvis$
    }).subscribe((results) => {
      const demandeValidation = results.demandeValidation.data;
      const demandeAvis = results.demandeAvis.data;

      // Calculate totals here
      const totalDemandeToValidate = demandeValidation.numberRecrutementToValidate + demandeValidation.numberRenouvellementToValidate;
      const grandTotal = totalDemandeToValidate + demandeAvis.totalDemandeAvis;

      this.validationCountsSubject.next({
        numberRecrutementToValidate: demandeValidation.numberRecrutementToValidate,
        numberRenouvellementToValidate: demandeValidation.numberRenouvellementToValidate,
        totalDemandeToValidate,
        demandeAvis: demandeAvis.demandeAvis,
        totalDemandeAvis: demandeAvis.totalDemandeAvis,
        numberRecrutementAvis: demandeAvis.numberRecrutementAvis,
        numberRenouvellementAvis: demandeAvis.numberRenouvellementAvis,
        total: grandTotal
      });
    });
  }

  /**
   * Gets the current validation counts without making a server request
   */
  getCurrentValidationCounts(): ValidationCounts {
    return this.validationCountsSubject.getValue();
  }

  /**
   * Updates the validation counts manually (useful after operations that affect counts)
   */
  updateValidationCounts(counts: ValidationCounts): void {
    this.validationCountsSubject.next(counts);
  }
}
